import { IApiBaseProps, IResultResponse, IUseFetchOptionsParams } from '@common/api.models';
import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { createUseFetchOptionsCustomPropsFn, createUseFetchOptionsFn } from '@common/selectOptions.services';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IItemResponse, IPagedResultsV1 } from '@gcl/react-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ICreateProjectCompanyRequest,
  IProjectCompany,
  IProjectCompanyWithRolesAndAllUserTeam,
  IUpdateProjectCompanyRequest,
} from './projectCompany.models';
import {
  createProjectCompanyAsync,
  deleteProjectCompanyAsync,
  getProjectCompanyAsync,
  getProjectCompanyOptionsAsync,
  IDeleteProjectCompanyProps,
  IGetProjectCompanyProps,
  updateProjectCompanyAsync,
  getCheckerCompanyOptionsAsync,
  getDesignerCompanyOptionsAsync,
  getReviewerCompanyOptionsAsync,
  getCompanyDistributionOptionsAsync,
  getAllProjectCompaniesWithRolesAndUsersAsync,
} from './projectCompany.services';

export const useFetchCompanyOptions = ({ isDirty = false, shouldConvertOptions = true }: IUseFetchOptionsParams) =>
  createUseFetchOptionsFn({
    queryName: FETCH_QUERIES_NAME.COMPANY_OPTIONS,
    getOptionsFn: getProjectCompanyOptionsAsync,
    enabled: !isDirty,
  });

export const useCreateProjectCompanyAsync = () => {
  return useMutation<IItemResponse<IProjectCompany>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.CREATE_PROJECT_COMPANY],
    async (createRequest: ICreateProjectCompanyRequest) => {
      const result = await createProjectCompanyAsync(createRequest);
      return result;
    },
  );
};

export const useUpdateProjectCompanyAsync = () => {
  return useMutation<IItemResponse<IProjectCompany>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_COMPANY],
    async (updateRequest: IUpdateProjectCompanyRequest) => {
      const result = await updateProjectCompanyAsync(updateRequest);
      return result;
    },
  );
};

export const useDeleteProjectCompanyAsync = () => {
  return useMutation<IResultResponse, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.DELETE_PROJECT_COMPANY],
    async (deleteProps: IDeleteProjectCompanyProps) => {
      const result = await deleteProjectCompanyAsync(deleteProps);
      return result;
    },
  );
};

export const useGetProjectCompanyAsync = ({ id, ...props }: IGetProjectCompanyProps, enabled = true) => {
  return useQuery<IItemResponse<IProjectCompany>, AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.PROJECT_COMPANY, id],
    async () => {
      const result = await getProjectCompanyAsync({ id, ...props });
      return result;
    },
    { enabled: isPositiveNumber(id) && isValidApiBaseProps(props) && enabled },
  );
};

export const useFetchCheckerCompanyOptions = ({
  isDirty = false,
  shouldConvertOptions = true,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsFn({
    queryName: FETCH_QUERIES_NAME.CHECKER_COMPANY_OPTIONS,
    getOptionsFn: getCheckerCompanyOptionsAsync,
    handleOptionsProps: { shouldConvertOptions },
    enabled: !isDirty,
  });

export const useFetchDesignerCompanyOptions = ({
  isDirty = false,
  shouldConvertOptions = true,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsFn({
    queryName: FETCH_QUERIES_NAME.DESIGNER_COMPANY_OPTIONS,
    getOptionsFn: getDesignerCompanyOptionsAsync,
    handleOptionsProps: { shouldConvertOptions },
    enabled: !isDirty,
  });

export const useFetchReviewerCompanyOptions = ({
  isDirty = false,
  shouldConvertOptions = true,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsFn({
    queryName: FETCH_QUERIES_NAME.REVIEWER_COMPANY_OPTIONS,
    getOptionsFn: getReviewerCompanyOptionsAsync,
    handleOptionsProps: { shouldConvertOptions },
    enabled: !isDirty,
  });

export const useFetchCompanyDistributionOptions = ({
  isDirty = false,
  shouldConvertOptions = true,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsCustomPropsFn({
    queryName: FETCH_QUERIES_NAME.COMPANY_DISTRIBUTION_OPTIONS,
    getOptionsFn: getCompanyDistributionOptionsAsync,
    handleOptionsProps: { shouldConvertOptions, textFieldName: 'name' },
    enabled: !isDirty,
  });

export const useGetAllProjectCompaniesWithRolesAndUsers = (props: IApiBaseProps, enabled = true) => {
  return useQuery<IPagedResultsV1<IProjectCompanyWithRolesAndAllUserTeam>, AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.PROJECT_COMPANY_ALL_WITH_ROLES_AND_USERS],
    async () => {
      const result = await getAllProjectCompaniesWithRolesAndUsersAsync(props);
      return result;
    },
    { enabled: isValidApiBaseProps(props) && enabled },
  );
};
